/* eslint-disable */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { StoryblokBlockComponent } from "../../lib/storyblok/types/StoryblokComponent";
import { GatedFormBlock } from "../../lib/storyblok/types/blocks/GatedFormBlock";
import cn from "classnames";
import Image from "next/image";
import { useInView } from "react-intersection-observer";
import s from "../../ui/CTAForm/CTAForm.module.scss";
import bs from "../../styles/block.spacing.module.scss";
import c from "../../styles/container.module.scss";
import { DynamicBlocks } from "../DynamicBlock";
import swooshDefaultTop from "../../public/images/divider-block-swoosh-default-top.png";
import swooshDefaultBottom from "../../public/images/divider-block-swoosh-default-bottom.png";
import swooshInvertedTop from "../../public/images/divider-block-swoosh-light-top.png";
import swooshInvertedBottom from "../../public/images/divider-block-swoosh-light-bottom.png";
import { imageSizes, isValidImageData } from "../../lib/utils/imageUtils";
import StoryblokImage from "../StoryblokImage";
import { useContentStyle } from "../../hooks/useStyle";
import BlockBackground from "../../ui/Common/BlockBackground";
import BlockIntro from "../../ui/Common/BlockIntro";
import Loader from "../../ui/Common/Loader";

const GatedForm: StoryblokBlockComponent<GatedFormBlock> = ({
  _uid,
  block,
  text,
  title,
  copy,
  image,
  formUrl,
  spacing,
  style,
  divider,
}) => {
  const contentStyle = useContentStyle(style);
  const isInverted = style === "" || style === undefined ? false : contentStyle;
  const [submitted, setSubmitted] = useState(false);
  const [presubmittedId, setPresubmittedId] = useState<string | null>(null);
  const spacingNormalized = spacing === undefined ? "both" : spacing;
  const [height, setHeight] = useState(1000);
  const [isFormLoaded, setIsFormLoaded] = useState(false);
  const [iframeUrl, setIframeUrl] = useState("");
  const [resizeAttempts, setResizeAttempts] = useState(0);
  const maxResizeAttempts = 3;

  const iframeRef = useRef<HTMLIFrameElement>(null);
  // Sometimes form urls contain go.Tobii.com (uppercase T in the url) due to translations. This little tweak addresses this.
  const normalizedFormUrl =
    formUrl && formUrl.includes("go.Tobii.com")
      ? formUrl.replace("go.Tobii.com", "go.tobii.com")
      : formUrl;
  const sanitizedFormUrl = normalizedFormUrl ? normalizedFormUrl.trim() : ""; // Ensure formUrl has no leading/trailing spaces
  const { ref: formRef, inView } = useInView({
    threshold: 0,
    triggerOnce: true,
    rootMargin: "300px",
  });

  let originUrl = "";
  if (sanitizedFormUrl) {
    try {
      originUrl = new URL(sanitizedFormUrl).origin;
    } catch (error) {
      console.error("Invalid formUrl:", sanitizedFormUrl);
    }
  }

  const buildUrlWithParams = (baseUrl: string): string => {
    if (typeof window === "undefined") return baseUrl;

    const fullPath = window.location.pathname.toLowerCase().replace(/^\//, "");
    const separator = baseUrl.includes("?") ? "&" : "?";
    return `${baseUrl}${separator}converting_page=${encodeURIComponent(
      fullPath
    )}#${_uid}`;
  };

  // Event handler for postMessages
  const onMessage = useCallback(
    (message) => {
      if (message.data.type === "pardotFormPreSubmit" && message.data.id) {
        if (!presubmittedId) {
          setPresubmittedId(message.data.id);
        }
      }

      if (message.data.type === "pardotFormSubmit" && presubmittedId) {
        setSubmitted(true);
        setPresubmittedId(null);
      }

      if (message.origin !== originUrl && !message.origin.includes("localhost"))
        return;

      // Handle Pardot ready signal - update URL with parameters
      if (message.data.type === "pardotFullyInitialized") {
        setIframeUrl(buildUrlWithParams(sanitizedFormUrl));
        return;
      }

      const resizeTypes = [
        `pardotFormResize-${sanitizedFormUrl}#${_uid}`,
        `ctaFormResize-${sanitizedFormUrl}#${_uid}`,
        `pardotFormResize-${sanitizedFormUrl}`,
        `ctaFormResize-${sanitizedFormUrl}`,
      ];

      const isResizeMessage =
        resizeTypes.some(
          (type) =>
            message.data.type && message.data.type.includes(type.split("#")[0])
        ) && typeof message.data.height === "number";

      if (isResizeMessage) {
        setHeight(message.data.height + 2);
        setIsFormLoaded(true);
        setResizeAttempts(0);
      }
    },
    [presubmittedId, _uid, sanitizedFormUrl, originUrl]
  );

  useEffect(() => {
    window.addEventListener("message", onMessage);

    return () => {
      window.removeEventListener("message", onMessage);
    };
  }, [onMessage]);

  // Initial load without parameters
  useEffect(() => {
    if (inView && sanitizedFormUrl && !iframeUrl) {
      const baseUrl = `${sanitizedFormUrl}#${_uid}`;
      setIframeUrl(baseUrl);
    }
  }, [inView, sanitizedFormUrl, _uid, iframeUrl]);

  useEffect(() => {
    if (
      inView &&
      iframeRef.current &&
      !isFormLoaded &&
      resizeAttempts < maxResizeAttempts
    ) {
      const timer = setTimeout(() => {
        try {
          if (iframeRef.current && iframeRef.current.contentWindow) {
            iframeRef.current.contentWindow.postMessage(
              { type: "requestResize" },
              "*"
            );
          }
        } catch (e) {}

        const adaptiveHeights = [500, 700, 1000];
        setHeight(
          adaptiveHeights[resizeAttempts] ||
            adaptiveHeights[adaptiveHeights.length - 1]
        );

        setResizeAttempts((prev) => prev + 1);

        if (resizeAttempts === maxResizeAttempts - 1) {
          setIsFormLoaded(true);
        }
      }, 1000 + resizeAttempts * 1000);

      return () => clearTimeout(timer);
    }
  }, [inView, isFormLoaded, resizeAttempts, iframeUrl]);

  const handleIframeLoad = () => {
    const timer = setTimeout(() => {
      if (!isFormLoaded) {
        try {
          if (iframeRef.current && iframeRef.current.contentWindow) {
            iframeRef.current.contentWindow.postMessage(
              { type: "requestResize" },
              "*"
            );
          }
        } catch (e) {}

        setHeight(700);
        setResizeAttempts(1);
      }
    }, 500);

    return () => clearTimeout(timer);
  };

  return !submitted ? (
    <div className={cn(s.ctaForm, bs[spacingNormalized], s[style])}>
      <BlockBackground style={style} opacity={100} />

      {divider === "swooshBlueTop" || divider === "swooshBlueBoth" ? (
        <div className={s.topImage}>
          <Image
            src={isInverted ? swooshInvertedTop : swooshDefaultTop}
            alt="Swoosh Top"
            objectFit="contain"
            layout="intrinsic"
            quality="100"
          />
        </div>
      ) : null}

      <div className={cn(c.wide, s.wrapper)}>
        <div className={cn(s.content)}>
          <BlockIntro
            alignment={"alignLeft"}
            inverted={isInverted}
            title={title}
            description={text}
            copy={copy}
            width={"full"}
            spaceHorizontal={false}
          />
          {image ? (
            <div className={cn(s.imageContainer)}>
              <>
                {isValidImageData(image) && (
                  <StoryblokImage
                    sizes={imageSizes("100vw", {
                      desktop: "960px",
                    })}
                    resize={{ width: 1200 }}
                    image={image}
                    layout="intrinsic"
                    contain
                    alt={title}
                  />
                )}
              </>
            </div>
          ) : null}
        </div>

        {sanitizedFormUrl && (
          <div className={cn(s.form)} ref={formRef}>
            <div>
              {inView ? (
                <iframe
                  title={`Form: ${sanitizedFormUrl}`}
                  ref={iframeRef}
                  width="100%"
                  height={height}
                  src={iframeUrl}
                  onLoad={handleIframeLoad}
                  style={{
                    opacity: isFormLoaded ? 1 : 0,
                    transition: "opacity 0.3s ease-in-out",
                    minHeight: "400px",
                  }}
                />
              ) : null}
              {(!inView || !isFormLoaded) && (
                <div
                  className={cn(s.loadingContainer)}
                  style={{
                    minHeight: inView ? "400px" : "auto",
                  }}
                >
                  <Loader variant="default" size="medium" />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {divider === "swooshBlueBottom" || divider === "swooshBlueBoth" ? (
        <div className={s.bottomImage}>
          <Image
            src={isInverted ? swooshInvertedBottom : swooshDefaultBottom}
            alt="Swoosh Bottom"
            objectFit="contain"
            layout="intrinsic"
            quality="100"
          />
        </div>
      ) : null}
    </div>
  ) : (
    <DynamicBlocks blocks={block ?? []} />
  );
};

export default GatedForm;
